<template>
  <Modal
    :title="title"
    :no-footer="true"
    :width="modalWidth"
    @hide-modal="emit('hide-modal')"
  >
    <div class="viewer-body" :class="{ 'has-rail': hasRail }">
      <div
        ref="containerRef"
        class="media-viewer-container"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <!-- Previous hover zone and button -->
        <div v-if="canGoPrevious" class="nav-zone nav-zone-prev">
          <button
            class="nav-button nav-button-prev"
            aria-label="Previous"
            @click="goPrevious"
          >
            <svg class="nav-button-icon" viewBox="0 0 24 24">
              <path d="M15.41 16.09l-4.58-4.59 4.58-4.59L14 5.5l-6 6 6 6z" />
            </svg>
          </button>
        </div>

        <!-- Next hover zone and button -->
        <div v-if="canGoNext" class="nav-zone nav-zone-next">
          <button
            class="nav-button nav-button-next"
            aria-label="Next"
            @click="goNext"
          >
            <svg class="nav-button-icon" viewBox="0 0 24 24">
              <path d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z" />
            </svg>
          </button>
        </div>

        <!-- Swipeable content wrapper -->
        <div
          class="swipe-content"
          :class="{
            'swipe-content-dragging': isDragging,
            'swipe-content-animating': isAnimating && !isDragging
          }"
          :style="swipeStyle"
        >
          <div
            v-if="loading"
            class="flex flex-col items-center justify-center p-8 text-center text-white"
          >
            <Spinner :message="loadingMessage" />
          </div>
          <div v-if="error" class="media-viewer-error">
            <i
              class="fa-solid fa-exclamation-triangle text-5xl mb-4 warning-icon"
            ></i>
            <p class="mb-4">{{ error }}</p>
            <SeraButton type="secondary" @click="emit('retry')">
              Try Again
            </SeraButton>
          </div>
          <slot :hidden-class="{ hidden: loading || error }" />
        </div>
      </div>

      <div v-if="hasRail" class="caption-rail-slot">
        <slot name="side-rail" />
      </div>

      <div class="viewer-foot">
        <div class="flex justify-between items-center">
          <a
            :href="downloadUrl"
            class="download-link"
            download
            @click="emit('download')"
          >
            <i class="fa-solid fa-download mr-2"></i>
            Download
          </a>
          <div class="flex items-center gap-4 text-sm text-gray-600">
            <span v-if="formattedSource">
              <i :class="sourceIcon" class="mr-1"></i>
              {{ formattedSource }}
            </span>
            <span>{{ formattedDate }}</span>
            <slot name="footer-end" />
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useSlots } from "vue";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import { useMediaSource } from "@/composables/useMediaSource";

interface MediaItem {
  source?: string;
  created_by_name?: string;
  created_at?: string;
}

const props = withDefaults(
  defineProps<{
    title: string;
    downloadUrl: string;
    media: MediaItem;
    loading: boolean;
    error: string | null;
    loadingMessage?: string;
    defaultIcon?: string;
    canGoPrevious?: boolean;
    canGoNext?: boolean;
    goPrevious?: () => void;
    goNext?: () => void;
  }>(),
  {
    loadingMessage: undefined,
    defaultIcon: undefined,
    canGoPrevious: false,
    canGoNext: false,
    goPrevious: undefined,
    goNext: undefined
  }
);

const emit = defineEmits<{
  "hide-modal": [];
  retry: [];
  download: [];
}>();

const { formattedSource, sourceIcon, formattedDate } = useMediaSource(
  () => props.media,
  props.defaultIcon || "fa-regular fa-file"
);

// A consumer that fills the side-rail slot (photo/video viewers) gets the
// caption rail + a wider, two-column layout on desktop; documents pass no rail
// and keep the original single-column modal.
const slots = useSlots();
const hasRail = computed(() => !!slots["side-rail"]);
const modalWidth = computed(() => (hasRail.value ? 1160 : 900));

// Touch gesture state
const containerRef = ref<HTMLElement | null>(null);
const touchStartX = ref<number | null>(null);
const touchStartY = ref<number | null>(null);
const dragOffsetX = ref(0);
const isDragging = ref(false);
const isAnimating = ref(false);

// Velocity tracking (PhotoSwipe-inspired)
const velocityX = ref(0);
const lastMoveTime = ref(0);
const lastMoveX = ref(0);
const VELOCITY_SAMPLE_INTERVAL = 50; // ms - sample velocity at this interval

// Thresholds
const SWIPE_DISTANCE_THRESHOLD = 50; // px - minimum distance to trigger swipe
const SWIPE_VELOCITY_THRESHOLD = 0.3; // px/ms - velocity that triggers swipe regardless of distance

// Computed style for swipe transform
const swipeStyle = computed(() => {
  if (dragOffsetX.value === 0) return {};
  return { transform: `translateX(${dragOffsetX.value}px)` };
});

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    emit("hide-modal");
    return;
  }

  if (isAnimating.value) return;

  if (event.key === "ArrowLeft" && props.canGoPrevious && props.goPrevious) {
    event.preventDefault();
    animateSlideTransition("previous");
  } else if (event.key === "ArrowRight" && props.canGoNext && props.goNext) {
    event.preventDefault();
    animateSlideTransition("next");
  }
}

function handleTouchStart(event: TouchEvent) {
  if (event.touches.length === 1 && !isAnimating.value) {
    touchStartX.value = event.touches[0].clientX;
    touchStartY.value = event.touches[0].clientY;
    lastMoveX.value = event.touches[0].clientX;
    lastMoveTime.value = Date.now();
    velocityX.value = 0;
  }
}

function handleTouchMove(event: TouchEvent) {
  // Prevent default only if we're tracking a swipe
  if (
    touchStartX.value !== null &&
    event.touches.length === 1 &&
    !isAnimating.value
  ) {
    const currentX = event.touches[0].clientX;
    const deltaX = currentX - touchStartX.value;
    const deltaY = event.touches[0].clientY - (touchStartY.value ?? 0);

    // Only handle horizontal swipe if it's the dominant direction
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      event.preventDefault();
      isDragging.value = true;

      // Track velocity at intervals (PhotoSwipe pattern)
      const now = Date.now();
      const elapsed = now - lastMoveTime.value;
      if (elapsed >= VELOCITY_SAMPLE_INTERVAL) {
        const displacement = currentX - lastMoveX.value;
        velocityX.value = displacement / elapsed;
        lastMoveX.value = currentX;
        lastMoveTime.value = now;
      }

      // Limit drag if can't go in that direction
      if (deltaX > 0 && !props.canGoPrevious) {
        // Trying to go previous but can't - add resistance
        dragOffsetX.value = deltaX * 0.3;
      } else if (deltaX < 0 && !props.canGoNext) {
        // Trying to go next but can't - add resistance
        dragOffsetX.value = deltaX * 0.3;
      } else {
        dragOffsetX.value = deltaX;
      }
    }
  }
}

function handleTouchEnd(event: TouchEvent) {
  if (touchStartX.value === null) return;

  const touchEndX = event.changedTouches[0].clientX;
  const touchEndY = event.changedTouches[0].clientY;
  const deltaX = touchEndX - touchStartX.value;
  const deltaY = touchEndY - (touchStartY.value ?? 0);

  // Calculate final velocity if we haven't sampled recently
  const now = Date.now();
  const elapsed = now - lastMoveTime.value;
  if (elapsed > 0 && elapsed < 100) {
    const displacement = touchEndX - lastMoveX.value;
    velocityX.value = displacement / elapsed;
  }

  // Reset touch tracking
  touchStartX.value = null;
  touchStartY.value = null;
  isDragging.value = false;

  // Determine if swipe should trigger navigation
  // Either: sufficient distance OR sufficient velocity (in same direction as drag)
  const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
  const velocityTriggered =
    Math.abs(velocityX.value) > SWIPE_VELOCITY_THRESHOLD &&
    Math.sign(velocityX.value) === Math.sign(deltaX);
  const distanceTriggered = Math.abs(deltaX) > SWIPE_DISTANCE_THRESHOLD;
  const swipeTriggered =
    isHorizontalSwipe && (velocityTriggered || distanceTriggered);

  const shouldGoNext = deltaX < 0 && props.canGoNext && props.goNext;
  const shouldGoPrevious =
    deltaX > 0 && props.canGoPrevious && props.goPrevious;

  if (swipeTriggered && (shouldGoNext || shouldGoPrevious)) {
    animateSlideTransition(deltaX > 0 ? "previous" : "next");
  } else {
    // Snap back to center with spring animation
    animateSnapBack();
  }

  velocityX.value = 0;
}

function animateSlideTransition(direction: "previous" | "next") {
  const containerWidth = containerRef.value?.offsetWidth || 400;
  const gap = 20;

  // Start animating - slide current content out
  isAnimating.value = true;
  const exitOffset =
    direction === "previous" ? containerWidth + gap : -(containerWidth + gap);

  // Animate current slide out
  dragOffsetX.value = exitOffset;

  // Wait for exit animation, then navigate and animate in
  setTimeout(() => {
    // Navigate to new content
    if (direction === "previous" && props.goPrevious) {
      props.goPrevious();
    } else if (direction === "next" && props.goNext) {
      props.goNext();
    }

    // Position new content off-screen (opposite side)
    isDragging.value = true; // Disable transition temporarily
    dragOffsetX.value =
      direction === "previous" ? -(containerWidth + gap) : containerWidth + gap;

    // Use RAF to ensure the position is applied before animating
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isDragging.value = false; // Re-enable transition
        dragOffsetX.value = 0; // Animate to center

        // Clear animating flag after animation completes
        setTimeout(() => {
          isAnimating.value = false;
        }, 300);
      });
    });
  }, 250); // Match CSS transition duration
}

function animateSnapBack() {
  isAnimating.value = true;
  dragOffsetX.value = 0;
  setTimeout(() => {
    isAnimating.value = false;
  }, 300);
}

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
/* Single column by default (and for documents, which pass no rail). With a
   rail, order the children explicitly: narrow = media → footer → caption;
   desktop (lg) = media + caption side-by-side with a full-width footer below. */
.viewer-body {
  @apply flex flex-col;
}

.viewer-foot {
  @apply py-2 mt-3;
}

.viewer-body.has-rail .media-viewer-container {
  order: 1;
}

.viewer-body.has-rail .viewer-foot {
  order: 2;
}

.viewer-body.has-rail .caption-rail-slot {
  order: 3;
  @apply mt-4 pt-4 border-t border-gray-200;
}

/* Shorten the media stage on narrow screens so the stacked caption stays
   reachable without a long scroll. */
@media (max-width: 1023px) {
  .viewer-body.has-rail .media-viewer-container {
    height: 340px;
  }

  .viewer-body.has-rail :slotted(.media-viewer-element) {
    max-height: 340px;
  }
}

@media (min-width: 1024px) {
  .viewer-body.has-rail {
    @apply flex-row flex-wrap;
  }

  .viewer-body.has-rail .media-viewer-container {
    flex: 1;
  }

  .viewer-body.has-rail .caption-rail-slot {
    order: 2;
    width: 300px;
    flex-shrink: 0;
    @apply mt-0 pt-0 pl-6 border-t-0;
  }

  .viewer-body.has-rail .viewer-foot {
    order: 3;
    @apply w-full mt-4 pt-4 border-t border-gray-200;
  }
}

.media-viewer-container {
  @apply relative w-full rounded flex items-center justify-center;
  @apply overflow-hidden;
  height: 500px;
  background: var(--sera-color-neutral-gray-dark-gray-900, #000);
  touch-action: pan-y pinch-zoom;
}

/* Swipeable content wrapper */
.swipe-content {
  @apply flex items-center justify-center w-full;
  /* Spring-like easing: fast start, gentle settle (PhotoSwipe-inspired) */
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.swipe-content-dragging {
  transition: none;
}

.swipe-content-animating {
  /* Slightly bouncier for snap-back */
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

/* Navigation hover zones */
.nav-zone {
  @apply absolute top-0 h-full z-10;
  @apply flex items-center;
  width: 20%;
}

.nav-zone-prev {
  @apply left-0 justify-start;
  padding-left: 12px;
}

.nav-zone-next {
  @apply right-0 justify-end;
  padding-right: 12px;
}

/* Navigation buttons */
.nav-button {
  @apply w-12 h-12 rounded-full;
  @apply flex items-center justify-center;
  @apply border-none cursor-pointer;
  @apply opacity-0 transition-opacity duration-200;
  background: rgba(0, 0, 0, 0.5);
}

.nav-button:hover {
  background: rgba(0, 0, 0, 0.7);
}

.nav-button:focus {
  @apply outline-none;
  background: rgba(0, 0, 0, 0.7);
}

/* Show button when hovering its zone */
.nav-zone:hover .nav-button {
  opacity: 1;
}

/* Always show on touch devices */
@media (hover: none) {
  .nav-button {
    opacity: 0.7;
  }
}

.nav-button-icon {
  @apply w-9 h-9;
  fill: white;
}

.media-viewer-error {
  @apply flex flex-col items-center justify-center p-8 text-center;
  color: var(--sera-color-neutral-gray-white-0, #fff);
}

.warning-icon {
  color: var(--sera-color-accents-yellow-900);
}

.download-link {
  @apply inline-flex items-center no-underline text-sm hover:underline;
  color: var(--sera-color-text-links);
}

/* Shared element styles for slotted content */
:slotted(.media-viewer-element) {
  @apply max-w-full block object-contain;
  max-height: 500px;
}

:slotted(.media-viewer-element.hidden) {
  @apply invisible absolute;
}

:slotted(.media-viewer-element.pdf-frame) {
  @apply w-full border-none relative;
  height: 500px;
  z-index: 1;
}

:slotted(.media-viewer-element.video-element) {
  @apply w-full;
}

:slotted(.media-viewer-pdf-fallback) {
  @apply text-center p-8;
  color: var(--sera-color-neutral-gray-white-0, #fff);
}

:slotted(.media-viewer-pdf-fallback a) {
  color: var(--sera-color-text-links);
}

:slotted(.media-viewer-unsupported) {
  @apply flex flex-col items-center justify-center p-8 text-center;
  color: var(--sera-color-neutral-gray-white-0, #fff);
}

:slotted(.media-viewer-unsupported i) {
  @apply mb-4;
  font-size: 4rem;
  color: var(--sera-color-neutral-gray-med-gray-500);
}

:slotted(.media-viewer-unsupported p) {
  @apply mb-4;
  color: var(--sera-color-neutral-gray-med-gray-400);
}
</style>
